// test-table-chrome — the test table beside the editor on the isolated assignment, challenge and
// guided-make surfaces (activity-test-table, Trello 2P8y4y9S).
//
// A TENTH sibling chrome module, NOT one of the uniformly-wired ones: it ALLOW-LISTS
// `body[data-surface]` to exactly `assignment | challenge | guided-make` (the only three types a
// test table can attach to) and is a no-op on every other surface and on a host with no
// `#test-table-config` — so an activity without a table is unchanged byte for byte.
//
// Wired LATE from main.js, inside `loadProject().then()` and after `surface.wire`, because it needs
// `core` (the student's OWN code runs in the VISIBLE runner) and the bundled Ace (the model answer
// and every mutant run in a second, OFF-SCREEN runner — `hidden-runner.js`, reused as-is). It is
// never wired on a LOCKED plan page (main.js returns before it; the server ships no config there
// either), so the table follows the plan rail's own locked treatment with no lock state of its own.
//
// It imports NO Lit and NO widget code: the `editor` bundle stays Lit-free. The widget is the
// light-DOM `<pc-test-table>` (src/islands/shared/pc-test-table.ts), loaded as its own shell entry
// (`/js/shell/pc-test-table.js`) by the three templates. This module creates the element, waits for
// it to be defined, hands it a view that carries NO hidden code (mutants as `{required, hint}`
// only), runs the suite through `test-table-grading.js` and POSTs the verdicts to `testTableRun`.
//
// Per-type gating UI (task 5.6):
//   assignment  — Submit (#assignment-submit) is disabled, with the reason, until every required
//                 mistake is caught under the current fingerprint (the server refuses too — a
//                 process gate, not a security boundary);
//   challenge   — an additive "checkpoints + tests" status; completion needs both (the server's
//                 composite gate). `challenge-chrome.js` reports each Check through
//                 `pc-challenge-checked`;
//   guided-make — advisory only; nothing is ever disabled.
//
// pgzero-test-tables (Trello TNUmFH7g) — who runs what (design D3):
//   python host, author mode  — UNCHANGED: the student's code in the visible runner, the model
//                               answer and mutants unwrapped in the hidden runner;
//   pgzero host, author mode  — EVERY source (the live main.py, the model answer, each mutant) is
//                               wrapped by `buildPgzeroWrapper` and called in the hidden python-
//                               mode runner (the worker only takes the function-call path in
//                               python mode); the visible game is stopped, never fenced;
//   own-function mode (python or pgzero) — the student picks one of their own functions; it is
//                               wrapped (stubs only on pgzero) and called in the hidden runner;
//                               no model answer, no mutants. Each Run re-lists the functions first.

import { createHiddenRunner, runWithTimeout, waitForReady, DEFAULT_CALL_TIMEOUT_MS } from './hidden-runner.js';
import {
  buildParseProgram, readParseOutput, observeFunctionResult, observeConsoleRun,
  coverage, duplicateRows, runSuite, stickyMutantsCaught, requiredStatus, toWire, rowsFromSaved, OWN,
  buildPgzeroWrapper, buildFunctionListProgram, readFunctionList, ownCompleteFor
} from './test-table-grading.js';

// The own-mode Submit `title` — the server's 403 sentence (U15).
var OWN_SUBMIT_TITLE = 'Run your testing table with at least one expected, one boundary and one invalid row, all passing, before you turn this in.';

var ALLOWED_SURFACES = ['assignment', 'challenge', 'guided-make'];
var WIDGET_LOAD_MS = 15000;
var PARSE_TIMEOUT_MS = 5000;

function readConfig(doc) {
  var el = doc.getElementById('test-table-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || 'null');
    return (cfg && typeof cfg === 'object' && cfg.configured === true) ? cfg : null;
  } catch (e) {
    return null;
  }
}

/** The widget's view of the config — every hidden thing (mutant code, model source) left out. */
export function publicView(cfg) {
  return {
    hostType: cfg.hostType || '',
    gating: cfg.gating || '',
    // pgzero-test-tables (5.0): own-function passes through, never collapsed to 'function'.
    target: cfg.target === 'stdin' ? 'stdin' : (cfg.target === 'own-function' ? 'own-function' : 'function'),
    ownFunction: cfg.ownFunction === true,
    lang: cfg.lang || '',
    functionName: cfg.functionName || '',
    params: Array.isArray(cfg.params) ? cfg.params.slice() : [],
    compare: cfg.compare || 'return',
    inputCount: cfg.inputCount || 1,
    minRows: cfg.minRows || {},
    maxRows: cfg.maxRows,
    mutants: (Array.isArray(cfg.mutants) ? cfg.mutants : []).map(function (m) {
      return { required: !!(m && m.required === true), hint: (m && m.hint) || '', title: (m && typeof m.title === 'string') ? m.title : '' };
    })
  };
}

function endpointFor(cfg) {
  return '/api/courses/' + encodeURIComponent(cfg.courseId)
    + '/lessons/' + encodeURIComponent(cfg.lessonId)
    + '/materials/' + encodeURIComponent(cfg.materialId) + '/testTableRun';
}

export function wireTestTable(opts) {
  try {
    var o = opts || {};
    var doc = o.doc || document;
    var core = o.core;
    var surface = doc.body ? (doc.body.getAttribute('data-surface') || 'editor') : 'editor';
    if (ALLOWED_SURFACES.indexOf(surface) === -1 || !core) { return null; }
    var cfg = readConfig(doc);
    if (!cfg) { return null; }
    var mount = doc.getElementById('test-table-mount');
    if (!mount) {
      console.error('test-table-chrome: #test-table-mount is missing');
      return null;
    }

    // guided-make: the steps were APPENDED into the same rail by guided-make-chrome.js (wired
    // before this), so move the table below them — the steps are the activity, the table extra.
    if (surface === 'guided-make' && mount.parentNode && mount.parentNode.id === 'guided-make-panel') {
      mount.parentNode.appendChild(mount);
    }

    var widget = doc.createElement('pc-test-table');
    mount.appendChild(widget);

    // pgzero-test-tables (5.0): own mode is "attached and ready" WITHOUT a model source.
    var ownMode = cfg.ownFunction === true;
    var lang = cfg.lang === 'pgzero' ? 'pgzero' : 'python';
    var wrapped = ownMode || lang === 'pgzero';   // the student's code runs in the HIDDEN runner
    var attached = cfg.attached === true && (ownMode || typeof cfg.modelSource === 'string');

    // Own mode: the student's CHOSEN function ({name, params, source}), the live list and its
    // state ('loading' | 'ready' | 'none' | 'syntax' | 'gone' | 'error'), and a one-line note.
    var savedOwn = ownMode && cfg.saved && typeof cfg.saved.functionName === 'string' && cfg.saved.functionName
      ? cfg.saved : null;
    var chosen = null;
    var fnList = [];
    var fnState = 'loading';
    var fnSyntaxLine = 0;
    // The file a syntax error is in (D16) and the functions the list left out (D22); `fnHidden`
    // is NOT `hidden`, the hidden runner (D25).
    var fnSyntaxFile = 'main.py';
    var fnHidden = [];
    var fnNote = null;
    // Has a function list EVER been applied? The first one restores the saved selection (or
    // auto-selects a lone function) whether it came from widget-ready or a later Refresh — so a
    // FAILED first list never strands the saved rows behind a "Change function?" confirm.
    var listedOnce = false;
    // Until a function is chosen, a restored run's columns are its SAVED parameters.
    var pendingParams = savedOwn && Array.isArray(savedOwn.params) ? savedOwn.params.slice() : [];

    // The EFFECTIVE config every grading call reads: in own mode the function name and
    // parameters come from the student's chosen function, never `cfg` (own-mode sanitising
    // leaves those empty); in author mode it is `cfg` itself.
    function eff() {
      if (!ownMode) { return cfg; }
      return Object.assign({}, cfg, {
        target: 'function',
        compare: 'return',
        functionName: chosen ? chosen.name : (savedOwn ? savedOwn.functionName : ''),
        params: chosen ? chosen.params.slice() : pendingParams.slice()
      });
    }
    function currentView() {
      if (!attached) { return { hostType: surface, gating: cfg.gating || '', target: 'function', mutants: [] }; }
      return publicView(eff());
    }

    var view = currentView();
    var target = attached ? (cfg.target === 'stdin' ? 'stdin' : 'function') : 'function';
    var rows = attached && cfg.saved ? rowsFromSaved(cfg.saved.rows, eff()) : [];
    var caught = attached && cfg.saved && !ownMode ? (cfg.saved.mutantsCaught || []).slice() : [];
    var evidence = {};   // this run's "which test caught which faulty copy" (display only, never saved)
    var results = [];
    function restoreResult(r) {
      // U10 — a stored verdict maps back to its own outcome.
      if (r.verdict === 'game-only') {
        var name = String(r.actualOutput || '').replace(/^Can't test: uses\s*/, '');
        return { own: OWN.GAME_ONLY, actual: '', gameOnly: name === 'a game object' ? '' : name };
      }
      if (r.verdict === 'timeout') { return { own: OWN.TIMEOUT, actual: r.actualOutput || '' }; }
      return { own: r.pass === true ? OWN.PASS : OWN.FAIL, actual: r.actualOutput || '' };
    }
    if (attached && cfg.saved && Array.isArray(cfg.saved.results)) {
      cfg.saved.results.forEach(function (r) {
        if (r && typeof r.rowIndex === 'number' && r.rowIndex < rows.length) {
          results[r.rowIndex] = restoreResult(r);
        }
      });
    }
    var ran = !!(cfg.saved && !cfg.saved.stale && cfg.saved.rows && cfg.saved.rows.length);
    // U12 — own mode's Submit gate reads the last SAVED completeness: on load the client mirror
    // over the saved run, then each POST response's server-recomputed `ownComplete`.
    var ownComplete = !!(savedOwn && ownCompleteFor(savedOwn.rows, savedOwn.results, cfg.minRows, savedOwn.functionName));
    var running = false;
    var hidden = null, hiddenFailed = false, visibleReady = false;
    var checkpointsPassed = cfg.checkpointsPassed === true;
    var completed = cfg.submissionState === 'completed';
    var stale = false;

    core.on('ready', function () { visibleReady = true; });

    // ── gating UI ─────────────────────────────────────────────────────────────────────────
    var submitBtn = surface === 'assignment' ? doc.getElementById('assignment-submit') : null;
    var submitBlocked = false;
    function applySubmitGate() {
      if (!submitBtn || cfg.preview === true || !attached) { return; }
      var status = requiredStatus(view.mutants, caught);
      submitBlocked = ownMode ? !ownComplete : !status.allRequiredCaught;
      if (submitBlocked) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('data-tests-blocked', 'true');
        submitBtn.title = ownMode ? OWN_SUBMIT_TITLE : 'Catch every required mistake with your tests to unlock Submit.';
      } else if (submitBtn.getAttribute('data-tests-blocked') === 'true') {
        submitBtn.removeAttribute('data-tests-blocked');
        submitBtn.disabled = false;
        submitBtn.title = '';
      }
    }
    // assignment-chrome re-enables Submit after a failed submit; a capture-phase guard keeps the
    // block honest whatever re-enabled the button (the server refuses the submit as well).
    if (submitBtn) {
      doc.addEventListener('click', function (e) {
        if (!submitBlocked) { return; }
        var t = e.target && e.target.closest ? e.target.closest('#assignment-submit') : null;
        if (t) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);
    }

    // U15 — own-mode notices (no mutants: completeness is the whole story).
    function ownNotice() {
      if (surface === 'assignment') {
        return ownComplete ? { text: 'Your testing table is complete — Submit is unlocked.', tone: 'success' } : { text: '', tone: 'info' };
      }
      if (surface === 'challenge') {
        if (completed) { return { text: 'Challenge complete — your checkpoints pass and your testing table is complete.', tone: 'success' }; }
        if (checkpointsPassed && !ownComplete) {
          return { text: 'Checkpoints passed — complete your testing table to complete the challenge.', tone: 'info' };
        }
        if (ownComplete && !checkpointsPassed) {
          return { text: 'Your testing table is complete. Pass the checkpoints (Check) to complete the challenge.', tone: 'info' };
        }
        return { text: '', tone: 'info' };
      }
      return ownComplete && ran ? { text: 'Your testing table is complete.', tone: 'success' } : { text: '', tone: 'info' };
    }

    function statusNotice() {
      if (!attached) { return { text: '', tone: 'info' }; }
      if (ownMode) { return ownNotice(); }
      if (stale) {
        return { text: 'Your teacher has changed the hidden mistakes. Reload the page and run your tests again.', tone: 'warning' };
      }
      if (cfg.saved && cfg.saved.stale && !ran) {
        return { text: 'Your teacher has changed the hidden mistakes since your last run. Run your tests again.', tone: 'warning' };
      }
      var st = requiredStatus(view.mutants, caught);
      if (surface === 'assignment') {
        return st.allRequiredCaught
          ? { text: 'You’ve caught every required mistake — Submit is unlocked.', tone: 'success' }
          : (ran ? { text: 'Keep testing: Submit unlocks once your tests catch every required mistake.', tone: 'info' } : { text: '', tone: 'info' });
      }
      if (surface === 'challenge') {
        if (completed) { return { text: 'Challenge complete — your checkpoints pass and your tests catch every required mistake.', tone: 'success' }; }
        if (checkpointsPassed && !st.allRequiredCaught) {
          var left = st.required - st.requiredCaught;
          return { text: 'Checkpoints passed — catch ' + left + ' more required mistake' + (left === 1 ? '' : 's') + ' to complete the challenge.', tone: 'info' };
        }
        if (st.allRequiredCaught && !checkpointsPassed) {
          return { text: 'Your tests catch every required mistake. Pass the checkpoints (Check) to complete the challenge.', tone: 'info' };
        }
        return { text: '', tone: 'info' };
      }
      return st.allRequiredCaught && ran
        ? { text: 'Nice testing — you caught every required mistake.', tone: 'success' }
        : { text: '', tone: 'info' };
    }

    function render(extra) {
      var e = extra || {};
      var n = statusNotice();
      view = currentView();
      widget.view = view;
      // Own mode: the chooser's state (pgzero-test-tables 6.1). Inert in author mode.
      // `gameUses` (pgzero hosts): the widget warns as soon as such a function is selected.
      widget.functions = fnList.map(function (f) {
        var uses = Array.isArray(f.gameUses) ? f.gameUses.slice() : [];
        return { name: f.name, params: f.params.slice(), gameUses: uses, file: f.file || 'main.py' };
      });
      widget.functionState = fnState;
      widget.syntaxLine = fnSyntaxLine;
      widget.syntaxFile = fnSyntaxFile;
      widget.hiddenFunctions = fnHidden.map(function (h) { return { name: h.name, reason: h.reason }; });
      widget.selectedFunction = chosen ? chosen.name : '';
      widget.functionNote = fnNote;
      // configured but no model answer linked => "not set up yet"; linked but its source could not
      // be loaded => "can't run right now".
      widget.mode = attached ? 'ready' : (cfg.attached === true ? 'unavailable' : 'not-configured');
      widget.preview = cfg.preview === true;
      widget.rows = rows;
      widget.results = results.slice();
      widget.caught = caught.slice();
      widget.evidence = evidence;
      widget.ran = ran;
      widget.busy = running;
      widget.progress = e.progress || '';
      widget.error = e.error || '';
      widget.notice = n.text;
      widget.noticeTone = n.tone;
      widget.readOnly = stale;
      var cov = coverage(rows, eff());
      widget.advice = {
        shortfalls: cov.shortfalls,
        duplicates: duplicateRows(rows),
        offBoundary: cov.offBoundaryRows
      };
      applySubmitGate();
    }

    // The challenge chrome reports every Check (passed + the server's submissionState).
    if (surface === 'challenge') {
      doc.addEventListener('pc-challenge-checked', function (ev) {
        var d = (ev && ev.detail) || {};
        checkpointsPassed = d.passed === true;
        if (d.submissionState === 'completed') { completed = true; }
        if (ready) { render(); }
      });
    }

    // ── the runners ───────────────────────────────────────────────────────────────────────
    // pgzero-test-tables (5.2 / U13): all THREE fencing pieces are python-only. A pgzero suite
    // stops the visible game at its start and never touches the runner controls (the student's
    // calls run in the hidden runner, so the visible one is free).
    var fences = lang !== 'pgzero';
    var runnerControlIds = ['runner-run', 'runner-stop', 'stage-prerun-btn'];
    function fenceRunnerControls() {
      if (!running || !fences) { return; }
      runnerControlIds.forEach(function (id) {
        var b = doc.getElementById(id);
        if (b) { b.disabled = true; }
      });
    }
    function releaseRunnerControls() {
      if (!fences) { return; }
      var r = doc.getElementById('runner-run');
      var s = doc.getElementById('runner-stop');
      if (r) { r.disabled = !visibleReady; }
      if (s) { s.disabled = true; }
      var p = doc.getElementById('stage-prerun-btn');
      if (p && r) { p.disabled = r.disabled || r.getClientRects().length === 0; }
    }
    core.on('state', fenceRunnerControls);
    core.on('ready', fenceRunnerControls);
    if (fences) {
      doc.addEventListener('click', function (e) {
        if (!running) { return; }
        var t = e.target && e.target.closest ? e.target.closest('#runner-run, #runner-stop, #stage-prerun-btn') : null;
        if (t) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    // The live main.py (D3): `core.getFiles()`, falling back to the active buffer.
    function liveMain() {
      try {
        var files = core.getFiles();
        if (Array.isArray(files)) {
          var main = files.filter(function (f) { return f && f.name === 'main.py'; })[0];
          if (main && typeof main.content === 'string') { return main.content; }
        }
      } catch (e) { /* fall back */ }
      try { return core.getSource() || ''; } catch (e2) { return ''; }
    }
    // One live project file by name (own-function-rooms-picker D2): main.py falls back to the
    // active buffer; any other file is null when the project does not have it.
    function liveFile(name) {
      if (name === 'main.py') { return liveMain(); }
      try {
        var files = core.getFiles();
        if (Array.isArray(files)) {
          var f = files.filter(function (x) { return x && x.name === name; })[0];
          if (f && typeof f.content === 'string') { return f.content; }
        }
      } catch (e) { /* absent */ }
      return null;
    }
    // The student's own source, wrapped for the hidden runner (stubs only on a pgzero host).
    // It wraps the CHOSEN function's file alone (main.py before any choice), D3 / D20 / D23.
    function wrappedOwnSource() {
      var file = chosen && chosen.file ? chosen.file : 'main.py';
      var src = liveFile(file);
      return buildPgzeroWrapper(src == null ? '' : src, { stubs: lang === 'pgzero', fileName: file });
    }
    function wrapHidden(src) {
      return lang === 'pgzero' ? buildPgzeroWrapper(src, { stubs: true }) : src;
    }

    function ensureHidden() {
      if (hidden) { return hidden.ready(); }
      // Own mode seeds the wrapped OWN source, never `cfg.modelSource` (5.0); a pgzero author
      // table seeds the wrapped model answer (runSuite swaps sources before every call anyway).
      var seed = ownMode ? wrappedOwnSource() : wrapHidden(cfg.modelSource || '');
      // `o.createHiddenRunner` is a test seam (host ESM specs); production passes none.
      var makeHidden = typeof o.createHiddenRunner === 'function' ? o.createHiddenRunner : createHiddenRunner;
      try {
        hidden = makeHidden({ doc: doc, ace: o.ace, mode: 'python', source: seed });
      } catch (err) {
        // A runner that cannot even be CREATED is "the checker could not start" too; `hidden`
        // stays null, so a later Refresh / Run tries again.
        hiddenFailed = true;
        throw err;
      }
      hiddenFailed = false;
      return hidden.ready().catch(function (err) { hiddenFailed = true; throw err; });
    }

    // ── own mode: the function list ───────────────────────────────────────────────────────
    // Resolves { functions } | { error:'syntax', line } | null (the list program gave no answer).
    function listFunctions() {
      // Deferred so a hidden runner that cannot even be CREATED rejects (-> the 'error' state,
      // Refresh enabled) instead of throwing out of the caller.
      return Promise.resolve().then(ensureHidden).then(function () {
        var mainSrc = liveFile('main.py');
        var roomsSrc = liveFile('rooms.py');
        // D15: one run PER FILE, IN SEQUENCE on the one hidden runner (concurrent requests hang).
        function one(name, src, otherName, otherSrc) {
          var opts = { lang: lang };
          if (otherSrc != null) { opts.other = { name: otherName, source: otherSrc }; }
          return hidden.runSource(buildFunctionListProgram(src, opts), [], PARSE_TIMEOUT_MS).then(function (res) {
            if (!res || res.status !== 'ok' || !res.value) { return null; }
            var parsed = readFunctionList(res.value.stdout);
            if (parsed && parsed.error === 'syntax') { parsed.file = name; }
            if (parsed && parsed.functions) {
              parsed.functions.forEach(function (f) { f.file = name; });
            }
            return parsed;
          });
        }
        return one('main.py', mainSrc, 'rooms.py', roomsSrc).then(function (a) {
          if (!a || a.error === 'syntax' || roomsSrc == null) { return a; }
          return one('rooms.py', roomsSrc, 'main.py', mainSrc).then(function (b) {
            if (!b || b.error === 'syntax') { return b; }
            // D18: main.py wins only when BOTH copies are listed.
            var listed = {};
            a.functions.forEach(function (f) { listed[f.name] = true; });
            var fns = a.functions.concat(b.functions.filter(function (f) { return !listed[f.name]; }));
            fns.forEach(function (f) { listed[f.name] = true; });
            var seen = {};
            var hid = (a.hidden || []).concat(b.hidden || []).filter(function (h) {
              if (listed[h.name] || seen[h.name]) { return false; }
              seen[h.name] = true;
              return true;
            });
            return { functions: fns, hidden: hid };
          });
        });
      });
    }
    function findFn(list, name) {
      return (Array.isArray(list) ? list : []).filter(function (f) { return f.name === name; })[0] || null;
    }
    // U7 — the chosen function now takes a different number of values: keep the selection,
    // rebuild the columns (pad / truncate every row's inputs), clear results, one-line note.
    function adoptParams(fresh) {
      var n = fresh.params.length;
      var changed = !chosen || chosen.params.length !== n;
      chosen = { name: fresh.name, params: fresh.params.slice(), source: fresh.source, file: fresh.file || 'main.py' };
      if (!changed) { return false; }
      rows = rows.map(function (r) {
        var inputs = (r.inputs || []).slice(0, n);
        while (inputs.length < n) { inputs.push(''); }
        return Object.assign({}, r, { inputs: inputs });
      });
      results = [];
      fnNote = { kind: 'params', name: fresh.name, count: n };
      return true;
    }
    // Apply a fresh list. The FIRST list that arrives (widget-ready, or a Refresh after a failed
    // or syntax-error first list) does the U8 auto-select + saved restore.
    function applyList(res) {
      if (!res) {
        // Never left at 'loading' (which disables the chooser and Refresh): 'error' keeps the
        // previous list (if any) and re-enables Refresh, so even a FIRST-load failure recovers
        // without a reload. The widget shows the "could not be listed" line for this state.
        fnState = 'error';
        fnSyntaxLine = 0;
        fnSyntaxFile = 'main.py';
        render(hiddenFailed
          ? { error: 'The checker could not start, so your tests could not be marked. Reload the page to try again.' }
          : undefined);
        return;
      }
      if (res.error === 'syntax') {
        fnState = 'syntax';
        fnSyntaxLine = res.line || 0;
        fnSyntaxFile = res.file || 'main.py';
        fnHidden = [];
        render();
        return;
      }
      fnList = res.functions;
      fnHidden = res.hidden || [];
      fnSyntaxLine = 0;
      fnSyntaxFile = 'main.py';
      fnState = fnList.length ? 'ready' : 'none';
      var first = !listedOnce;
      listedOnce = true;
      if (first) {
        var want = savedOwn ? findFn(fnList, savedOwn.functionName) : null;
        if (want) {
          // Restore the saved function (its saved rows already carry its saved columns).
          chosen = { name: want.name, params: pendingParams.slice(), source: want.source, file: want.file || 'main.py' };
          if (want.params.length !== pendingParams.length) { adoptParams(want); }
          else { chosen.params = want.params.slice(); }
        } else if (!savedOwn && fnList.length === 1) {
          chosen = { name: fnList[0].name, params: fnList[0].params.slice(), source: fnList[0].source, file: fnList[0].file || 'main.py' };
        }
        // A saved function that no longer exists: no selection, the saved rows stay until the
        // student picks one (then the widget's change-confirm applies).
      } else if (chosen) {
        var again = findFn(fnList, chosen.name);
        if (again) { adoptParams(again); }
      }
      render();
    }
    function refreshList(initial) {
      if (!ownMode || !attached) { return Promise.resolve(); }
      if (!initial) { fnNote = null; }
      return listFunctions().then(function (res) { applyList(res); }, function (err) {
        console.error(err);
        applyList(null);
      });
    }
    function ensureVisible() {
      if (visibleReady) { return Promise.resolve(); }
      return waitForReady(core);
    }
    function observe(res) {
      if (!res || res.status !== 'ok') { return { kind: 'timeout' }; }
      if (target === 'stdin') { return observeConsoleRun(res.value); }
      var list = (res.value && res.value.results) || [];
      return observeFunctionResult(list[0]);
    }
    // The student's OWN code: the visible runner, each row bounded by the same 2 s per-call
    // budget (and the same stop-and-restart containment) as the hidden runner.
    function runOwn(r) {
      // pgzero / own mode: the WRAPPED student source is already loaded in the hidden runner
      // (runSuite's `ownSource`), so the call goes there.
      if (wrapped) { return runHidden(r); }
      var start = target === 'stdin'
        ? function () { return core.requestConsoleRun(r.stdin); }
        : function () { return core.requestFunctionCall([{ functionName: cfg.functionName, args: r.args }]); };
      return runWithTimeout(core, start, DEFAULT_CALL_TIMEOUT_MS).then(observe);
    }
    function runHidden(r) {
      var p = target === 'stdin'
        ? hidden.consoleRun(r.stdin, DEFAULT_CALL_TIMEOUT_MS)
        : hidden.callFunction(eff().functionName, r.args, DEFAULT_CALL_TIMEOUT_MS);
      return p.then(observe);
    }

    function post(wire) {
      return fetch(endpointFor(cfg), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wire)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) { throw new Error((body && body.message) || ('testTableRun failed (' + res.status + ')')); }
          return body;
        });
      });
    }

    widget.addEventListener('pc-test-table-change', function (ev) {
      rows = (ev && ev.detail && ev.detail.rows) || [];
      results = [];   // the last run's verdicts no longer describe these rows
      render();
    });

    // Own mode: the student picked a function (the widget has ALREADY confirmed clearing rows).
    widget.addEventListener('pc-test-table-choose', function (ev) {
      if (!ownMode || running) { return; }
      var name = ev && ev.detail && ev.detail.name;
      var fn = findFn(fnList, name);
      if (!fn) { return; }
      chosen = { name: fn.name, params: fn.params.slice(), source: fn.source, file: fn.file || 'main.py' };
      rows = [];
      results = [];
      fnNote = null;
      if (fnState === 'gone' || fnState === 'error') { fnState = 'ready'; }
      render();
    });
    widget.addEventListener('pc-test-table-refresh', function () {
      if (!ownMode || running) { return; }
      fnState = 'loading';
      render();
      refreshList(false);
    });

    // U6 — every own-mode Run re-lists the functions on the live main.py FIRST: a syntax error
    // or a missing chosen function aborts; otherwise that run's fresh source/params are used.
    function prepareOwnRun() {
      if (!ownMode) { return Promise.resolve(true); }
      return listFunctions().then(function (res) {
        if (!res) { throw new Error('list-failed'); }
        if (res.error === 'syntax') {
          fnState = 'syntax';
          fnSyntaxLine = res.line || 0;
          fnSyntaxFile = res.file || 'main.py';
          fnHidden = [];
          return false;
        }
        fnList = res.functions;
        fnHidden = res.hidden || [];
        fnSyntaxFile = 'main.py';
        fnState = fnList.length ? 'ready' : 'none';
        var fresh = chosen ? findFn(fnList, chosen.name) : null;
        // D20: a same-name function that moved to the other file is a different function.
        if (fresh && chosen && (fresh.file || 'main.py') !== (chosen.file || 'main.py')) { fresh = null; }
        if (!fresh) {
          fnNote = { kind: 'gone', name: chosen ? chosen.name : '' };
          fnState = 'gone';
          chosen = null;
          return false;
        }
        // A changed parameter list rebuilds the columns — the student checks their inputs first.
        if (adoptParams(fresh)) { return false; }
        fnNote = null;
        return true;
      });
    }

    widget.addEventListener('pc-test-table-run', function (ev) {
      if (running || !attached || stale) { return; }
      if (ownMode && !chosen) { return; }
      rows = (ev && ev.detail && ev.detail.rows) || rows;
      if (!rows.length) { return; }
      running = true;
      fenceRunnerControls();
      // pgzero: the student's game is stopped while the hidden runner works (never fenced).
      if (lang === 'pgzero') { try { core.stop(); } catch (e) { /* not running */ } }
      render({ progress: 'Getting the checker ready…' });
      var functionSource = '';
      // Both deferred: a SYNCHRONOUS throw (createHiddenRunner) must reject into the catch below,
      // which clears `running` — never escape the listener and leave the table busy.
      Promise.all([
        wrapped ? Promise.resolve() : Promise.resolve().then(ensureVisible),
        Promise.resolve().then(ensureHidden)
      ]).then(function () {
        return prepareOwnRun();
      }).then(function (go) {
        if (!go) { return { ok: false, reason: 'aborted' }; }
        functionSource = chosen ? chosen.source : '';
        return runSuite({
          cfg: eff(),
          rows: rows,
          progress: function (text) { render({ progress: text }); },
          parse: function (raw) {
            return hidden.runSource(buildParseProgram(raw), [], PARSE_TIMEOUT_MS).then(function (res) {
              if (!res || res.status !== 'ok' || !res.value) { return null; }
              return readParseOutput(res.value.stdout, raw.length);
            });
          },
          // pgzero-test-tables (4.5a): the wrapped live source before the own phase (pgzero or
          // own mode), own mode skips the model/mutant phases, pgzero wraps every hidden source.
          ownSource: wrapped ? wrappedOwnSource() : null,
          ownOnly: ownMode,
          wrap: lang === 'pgzero' ? wrapHidden : null,
          own: runOwn,
          useSource: function (src) { return hidden.setSource(src); },
          model: runHidden,
          mutant: runHidden
        });
      }).then(function (res) {
        if (res && res.reason === 'aborted') {
          running = false;
          releaseRunnerControls();
          render();
          return;
        }
        if (!res || !res.ok) { throw new Error((res && res.reason) || 'run-failed'); }
        results = res.rows;
        caught = ownMode ? [] : stickyMutantsCaught(caught, res.caught, cfg.fingerprint, cfg.fingerprint);
        evidence = res.evidence || {};
        ran = true;
        running = false;
        releaseRunnerControls();
        var passes = res.rows.filter(function (r) { return r.own === OWN.PASS; }).length;
        render({ progress: 'Done: your code matched ' + passes + ' of ' + res.rows.length + ' predictions.' });
        var wire = toWire({ cfg: eff(), parent: cfg.parent, rows: rows, results: res.rows, mutantsCaught: caught,
          functionSource: functionSource });
        return post(wire).then(function (body) {
          if (body && body.stale) {
            stale = true;
            render();
            return;
          }
          if (body && body.answers && Array.isArray(body.answers.mutantsCaught) && !ownMode) {
            caught = body.answers.mutantsCaught.slice();
          }
          // U12 — the server's recomputed completeness is what the Submit gate reads.
          if (ownMode && body && typeof body.ownComplete === 'boolean') { ownComplete = body.ownComplete; }
          if (body && body.submissionState === 'completed') { completed = true; }
          render({ progress: widget.progress });
        }, function (err) {
          console.error(err);
          render({ progress: widget.progress, error: cfg.preview === true ? '' : 'Your results could not be saved. Run your tests again.' });
        });
      }).catch(function (err) {
        console.error(err);
        running = false;
        releaseRunnerControls();
        var msg = hiddenFailed
          ? 'The checker could not start, so your tests could not be marked. Reload the page to try again.'
          : (err && err.message === 'parse-failed')
            ? 'Your inputs could not be read this time. Run your tests again.'
            : 'Something went wrong while running your tests. Run them again.';
        render({ error: msg });
      });
    });

    // `whenDefined` never settles if the widget script 404s, so a deadline turns that into a
    // visible message instead of a silently empty panel.
    var ready = false;
    var defined = (window.customElements && typeof window.customElements.whenDefined === 'function')
      ? new Promise(function (resolve, reject) {
        var t = window.setTimeout(function () { reject(new Error('pc-test-table did not load')); }, WIDGET_LOAD_MS);
        window.customElements.whenDefined('pc-test-table').then(function () { window.clearTimeout(t); resolve(); });
      })
      : Promise.reject(new Error('custom elements unavailable'));

    // Submit is gated from the start, before the widget has even loaded.
    applySubmitGate();

    return defined.then(function () {
      ready = true;
      render();
      // U8 — own mode lists the student's functions once the widget is ready.
      if (ownMode && attached) { refreshList(true); }
      return widget;
    }).catch(function (err) {
      mount.textContent = 'The test table could not load. Reload the page to try again.';
      console.error(err);
      return null;
    });
  } catch (e) {
    // MUST NEVER THROW — it runs after the surface chrome in main.js.
    console.error(e);
    return null;
  }
}
